/**
 * Project share modal — list current collaborators, add by email,
 * and remove individual shares. Owner-only UI; the backend enforces
 * the same constraint on each endpoint.
 */
import { listProjectShares, addProjectShare, removeProjectShare } from '../api/projects.js';
import { toast } from './toast.js';

export async function openProjectShareModal(project) {
  document.querySelector('#projectShareModal')?.remove();

  let shares = [];
  try { shares = await listProjectShares(project.id); }
  catch (e) { toast(`Couldn't load shares: ${e.message}`); return; }

  document.body.insertAdjacentHTML('beforeend', renderShell(project, shares));
  const root = document.getElementById('projectShareModal');
  root.addEventListener('click', e => onClick(e, project, root));

  const form = root.querySelector('#shareForm');
  form?.addEventListener('submit', e => onAdd(e, project, root));
}

function renderShell(project, shares) {
  return `
    <div class="modal-bg show" id="projectShareModal">
      <div class="modal" style="width:460px">
        <h3>Share "${escape(project.name)}"</h3>
        <p style="font-size:12px;color:var(--text3);margin-top:4px">
          Invite teammates by email. Editors can update the project state; viewers can only open it.
        </p>

        <form id="shareForm" style="display:flex;gap:8px;margin-top:14px">
          <input type="email" id="shareEmail" placeholder="teammate@example.com" required
                 style="flex:1;padding:8px 10px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;color:var(--text);font-size:13px">
          <select id="shareRole" style="padding:8px 10px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;color:var(--text);font-size:13px">
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <button class="btn btn-primary btn-sm" type="submit">Invite</button>
        </form>

        <div id="shareList" style="margin-top:18px;max-height:280px;overflow-y:auto;display:flex;flex-direction:column;gap:6px">
          ${renderList(shares)}
        </div>

        <div style="text-align:right;margin-top:18px">
          <button class="btn" data-action="closeShareModal">Close</button>
        </div>
      </div>
    </div>
  `;
}

function renderList(shares) {
  if (shares.length === 0) {
    return `<div style="font-size:12.5px;color:var(--text3);font-style:italic;font-family:var(--font-display);padding:8px 2px">
      No collaborators yet. Invite someone above.
    </div>`;
  }
  return shares.map(s => `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 12px;border:1px solid var(--border);border-radius:6px;background:var(--bg3)">
      <div style="min-width:0">
        <div style="font-size:12.5px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
          ${escape(s.user_full_name || s.user_email)}
        </div>
        <div style="font-size:10.5px;color:var(--text3);font-family:var(--mono)">${escape(s.user_email)} · ${s.role}</div>
      </div>
      <button class="btn-sm btn-outline" data-remove-share="${s.user_id}" title="Remove">✕</button>
    </div>
  `).join('');
}

async function onClick(e, project, root) {
  if (e.target.closest('[data-action="closeShareModal"]')) {
    root.remove(); return;
  }
  const remove = e.target.closest('[data-remove-share]');
  if (remove) {
    const userId = +remove.dataset.removeShare;
    if (!confirm('Remove this collaborator?')) return;
    try {
      await removeProjectShare(project.id, userId);
      const fresh = await listProjectShares(project.id);
      root.querySelector('#shareList').innerHTML = renderList(fresh);
      toast('Removed');
    } catch (err) { toast(`Could not remove: ${err.message}`); }
  }
}

async function onAdd(e, project, root) {
  e.preventDefault();
  const email = root.querySelector('#shareEmail').value.trim();
  const role  = root.querySelector('#shareRole').value;
  if (!email) return;
  try {
    await addProjectShare(project.id, email, role);
    root.querySelector('#shareEmail').value = '';
    const fresh = await listProjectShares(project.id);
    root.querySelector('#shareList').innerHTML = renderList(fresh);
    toast(`Invited ${email}`);
  } catch (err) {
    toast(err.message);
  }
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
